// Auth flow:
//  1. App mount checks localStorage for `hook_token`.
//  2. If missing -> redirect browser to GET /auth/login (server bounces to login.telebroad.com).
//  3. login.telebroad.com sends user back to /auth/callback with the token.
//     - Preferred: #token=... in the URL fragment (not sent to server, not in logs).
//     - Accepted:  ?token=... in the query string (what tb-auth actually does today).
//  4. Callback page reads either, stores the token, strips it from the URL, navigates to /.
//  5. Every API call sends `Authorization: Bearer <token>`.
//
// localStorage was chosen over a cookie because login.telebroad.com (different origin)
// can't set a cookie on this origin -- the only cross-origin handoff that doesn't
// require shared cookie domain config on tb-auth's side is a redirect-with-token.

const TOKEN_KEY  = 'hook_token';
const USER_KEY   = 'hook_user';
// sessionStorage so it's scoped to this tab and the entry is gone after
// the tab closes -- but it survives the cross-origin auth round-trip
// (localhost -> login.telebroad.com -> /auth/callback) because the SPA
// is reloaded in the same tab. Persisting in localStorage would mean a
// user opening a new tab tomorrow gets bounced to last week's URL.
const RETURN_KEY = 'hook_return_to';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getUser() {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function setUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

// Save the current path+query+hash so /auth/callback can drop the user
// back where they were after a successful login. Skipped for the public
// landing and the auth pages themselves (saving "/" or "/auth/callback"
// would be useless or pointing at the wrong place).
export function rememberReturnTo() {
  const p = window.location.pathname + window.location.search + window.location.hash;
  const path = window.location.pathname;
  if (path === '/' || path === '/login' || path.startsWith('/auth/')) return;
  try { sessionStorage.setItem(RETURN_KEY, p); } catch {}
}

// Pop the saved return URL (single-use). Falls back to /captures so the
// caller always has somewhere to land. Returns a relative URL.
export function consumeReturnTo() {
  let v = null;
  try {
    v = sessionStorage.getItem(RETURN_KEY);
    if (v) sessionStorage.removeItem(RETURN_KEY);
  } catch {}
  // Belt-and-braces: only honour same-origin relative paths -- never let
  // a value like "//evil.example/" or "https://..." sneak through.
  if (typeof v === 'string' && v.startsWith('/') && !v.startsWith('//')) return v;
  return '/captures';
}

export function loginRedirect() {
  // Save where the user is BEFORE we leave this origin, so /auth/callback
  // can navigate back here after the login round-trip completes.
  rememberReturnTo();
  // Server-side handler builds the login.telebroad.com URL with the right callback.
  window.location.href = '/auth/login';
}

// Reads `token` from either the URL fragment (#token=...) or the query string
// (?token=...) depending on what the auth issuer used. Stores it and rewrites
// the URL so a refresh doesn't re-process it (and so the token doesn't sit in
// the address bar / browser history).
export function consumeTokenFromCallback() {
  // Fragment first (preferred -- not sent to server, not in logs).
  const hash = window.location.hash.replace(/^#/, '');
  if (hash) {
    const token = new URLSearchParams(hash).get('token');
    if (token) {
      setToken(token);
      history.replaceState(null, '', window.location.pathname + window.location.search);
      return token;
    }
  }
  // Fall back to query string -- this is what tb-auth currently uses.
  const qs = new URLSearchParams(window.location.search);
  const token = qs.get('token');
  if (token) {
    setToken(token);
    qs.delete('token');
    const rest = qs.toString();
    history.replaceState(null, '', window.location.pathname + (rest ? '?' + rest : ''));
    return token;
  }
  return null;
}

// Backwards-compatible alias (was named for the fragment-only flow).
export const consumeTokenFromFragment = consumeTokenFromCallback;

// Classify a server-returned auth error message into a stable code the SPA
// can branch on. The strings come from golang-jwt/jwt -- stable enough that
// substring matching is fine, and re-classifying here keeps the rest of the
// app from caring about JWT phrasing.
function classifyAuthError(reason) {
  const r = (reason || '').toLowerCase();
  if (r.includes('not valid yet')) return 'not_yet_valid'; // nbf in the future (clock skew)
  if (r.includes('expired'))       return 'expired';       // exp in the past
  if (r.includes('missing bearer')) return 'no_token';
  return 'invalid'; // bad signature, malformed, wrong algo, etc.
}

// fetchMe validates the stored JWT against /auth/me and returns:
//   { user, code: null }                    on success
//   { user: null, code: 'not_yet_valid' }   token kept; caller should retry
//   { user: null, code: 'expired'|'invalid'|'no_token'|'error' } token cleared
//
// The token is preserved on `not_yet_valid` because that's a transient
// clock-skew condition (Docker Desktop / WSL sometimes drift after sleep);
// a few seconds later the same token will validate. Forcing the user back
// through tb-auth in that window is bad UX with no security upside.
export async function fetchMe() {
  const token = getToken();
  if (!token) return { user: null, code: 'no_token' };
  let res;
  try {
    res = await fetch('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    return { user: null, code: 'error', message: e.message };
  }
  if (res.status === 401) {
    let reason = '';
    try {
      const data = await res.json();
      reason = String(data?.error || '');
    } catch {
      reason = (await res.text().catch(() => '')) || res.statusText;
    }
    const code = classifyAuthError(reason);
    if (code !== 'not_yet_valid') clearAuth();
    return { user: null, code, message: reason };
  }
  if (!res.ok) {
    return { user: null, code: 'error', message: res.statusText };
  }
  const user = await res.json();
  setUser(user);
  return { user, code: null };
}
